/**
 * @file ExportUtils.js
 * @description Hilfsfunktionen für den Export von Bot-Nachrichten.
 *
 * Unterstützte Exportformate:
 *  - E-Mail-Entwurf  (.eml)  → exportAsEmailDraft
 *  - CSV                     → exportAsCsv
 *  - Zwischenablage          → copyToClipboard
 *
 * ─── E-Mail-Export-Ablauf ────────────────────────────────────────────────────
 *
 *  Bot-Nachricht (HTML)
 *       │
 *       ▼  removeHtmlTags()
 *  Plaintext
 *       │
 *       ▼  extractEmailWithLLM()   →  POST /chat/extract-email
 *  E-Mail-Block gefunden?
 *       │
 *       ├─ Ja  → nur diesen Block exportieren (Hinweise/Rückfragen entfernt)
 *       │
 *       └─ Nein → Bestätigungs-Dialog
 *                   ├─ Ja  → gesamten Text + Signatur exportieren
 *                   └─ Nein → Abbruch
 *       │
 *       ▼  Betreff ermitteln (Priorität):
 *          1. "Betreff:"-Zeile im Text  (extractEmailSubjectLine)
 *          2. LLM generiert Betreff     (generateSubjectWithLLM → POST /chat/generate-subject)
 *          3. Erste sinnvolle Zeile     (extractSubject)
 *          4. Fallback: "Anfrage Robyflow"
 *       │
 *       ▼  openMailtoOrFallback()
 *  mailto:-Link (window.location.href) → öffnet Standard-Mail-Client im Verfassen-Modus ✓
 *  Fallback bei URL > 4000 Zeichen: Text in Zwischenablage + Hinweis-Dialog
 *
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Backend-Endpunkte (server/routers/chat/router.py):
 *  POST /chat/extract-email    – LLM extrahiert den reinen E-Mail-Block
 *  POST /chat/generate-subject – LLM generiert einen passenden Betreff
 */

import { api } from '../api/robyBackend';

// ─── LLM-Hilfsfunktionen ─────────────────────────────────────────────────────

/**
 * Sendet den Text an den Backend-Endpoint `/chat/extract-email`.
 * Das LLM erkennt dort – unabhängig von Formatierung oder Formalitätsgrad –
 * ob ein E-Mail-Inhalt vorhanden ist und gibt ausschließlich diesen zurück.
 *
 * Hintergrund: Reine Regex-Erkennung scheitert bei informellen E-Mails
 * („Hey, schick mir bitte …") oder wenn der Bot seine Antwort mit
 * Einleitungstext, Quellenhinweisen und Rückfragen umrahmt.
 *
 * @param {string} text - Bereinigter Plaintext der Bot-Nachricht
 * @returns {Promise<string|null>} Extrahierter E-Mail-Block oder null
 */
const extractEmailWithLLM = async (text) => {
  try {
    const response = await api.post('/chat/extract-email', { text });
    return response.data?.email || null;
  } catch (error) {
    // Fehler sichtbar machen – bei einem API-Fehler würde sonst fälschlicherweise
    // der "keine E-Mail erkannt"-Dialog erscheinen, obwohl eine E-Mail vorhanden ist.
    console.error('LLM-E-Mail-Extraktion fehlgeschlagen:', error);
    // Fehler nach oben weiterreichen, damit exportAsEmailDraft ihn abfangen kann
    throw error;
  }
};

/**
 * Sendet den E-Mail-Text an den Backend-Endpoint `/chat/generate-subject`.
 * Das LLM formuliert einen prägnanten Betreff (max. 80 Zeichen).
 *
 * Wird nur aufgerufen, wenn der E-Mail-Text keine explizite „Betreff:"-Zeile
 * enthält (z. B. bei informellen E-Mails).
 *
 * @param {string} text - E-Mail-Text (bereits extrahierter Block)
 * @returns {Promise<string|null>} Generierter Betreff oder null bei Fehler
 */
const generateSubjectWithLLM = async (text) => {
  try {
    const response = await api.post('/chat/generate-subject', { text });
    return response.data?.subject || null;
  } catch (error) {
    console.warn('LLM-Betreffgenerierung fehlgeschlagen, nutze Fallback:', error);
    return null;
  }
};

// ─── Öffentliche Export-Funktionen ───────────────────────────────────────────

export const downloadAsEml = (subject, content) => {
  const emlContent = [
    'From: ',
    `Subject: ${subject}`,
    'MIME-Version: 1.0',
    'Content-Type: text/plain; charset=UTF-8',
    'Content-Transfer-Encoding: 8bit',
    '',
    content,
  ].join('\r\n');

  const blob = new Blob([emlContent], { type: 'message/rfc822' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${subject.replace(/[/\\?%*:|"<>]/g, '-')}.eml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Exportiert eine Bot-Nachricht als E-Mail-Entwurf (.eml-Datei).
 *
 * Kernlogik:
 *  1. HTML bereinigen
 *  2. LLM extrahiert den reinen E-Mail-Block (ohne Bot-Kommentare)
 *  3. Wenn kein E-Mail-Block → Bestätigungs-Dialog
 *  4. Betreff ermitteln (Betreff:-Zeile → LLM → erste Zeile → Fallback)
 *  5a. mailto:-Link öffnet direkt das Verfassen-Fenster des Standard-Mail-Clients
 *  5b. Fallback bei zu langem Text: .eml-Datei herunterladen
 *
 * Warum mailto: statt .eml?
 *  .eml-Dateien öffnen sich in Outlook/Mail immer als empfangene Nachricht,
 *  nie als editierbarer Entwurf. mailto: öffnet direkt das Neue-E-Mail-Fenster.
 *
 * @param {string} messageText - HTML-Inhalt der Bot-Nachricht
 * @param {string} [subject='Anfrage Robyflow'] - Fallback-Betreff
 * @param {string} [userName=''] - Vor- und Nachname des eingeloggten Nutzers
 *                                  für die Unterschrift (aus Django-User-Modell).
 *                                  Leer → Fallback "Robyflow".
 */
export const exportAsEmailDraft = async (messageText, subject = 'Anfrage Robyflow', userName = '') => {
  if (!messageText) {
    console.error('Keine Nachricht zum Exportieren vorhanden');
    return;
  }

  try {
    // 1. HTML bereinigen
    const plainText = removeHtmlTags(messageText);

    // 2. LLM extrahiert den reinen E-Mail-Block (formal & informell)
    let emailBlock = null;
    try {
      emailBlock = await extractEmailWithLLM(plainText);
    } catch (llmError) {
      alert('Der E-Mail-Erkennungsdienst ist momentan nicht verfügbar.\nBitte versuche es erneut.');
      return;
    }

    // 3. Kein E-Mail-Inhalt erkannt → Nutzer fragen
    if (!emailBlock) {
      const confirmed = window.confirm(
        'In dieser Nachricht wurde keine E-Mail erkannt.\nTrotzdem als E-Mail-Entwurf exportieren?'
      );
      if (!confirmed) return;
    }

    const textForExport = emailBlock ?? plainText;

    // 4. Betreff ermitteln (Priorität: Betreff:-Zeile → LLM → erste Zeile → Fallback)
    const subjectFromBlock = extractEmailSubjectLine(textForExport);
    let finalSubject = subjectFromBlock;

    if (!finalSubject) {
      const llmSubject = await generateSubjectWithLLM(textForExport);
      finalSubject = llmSubject || extractSubject(textForExport) || subject;
    }

    // 5. Inhalt aufbereiten
    //    - E-Mail-Block: direkt verwenden (LLM hat ihn bereits bereinigt)
    //    - Kein Block:   gesamten Text mit Grußformel + Unterschrift versehen
    const emailContent = emailBlock
      ? emailBlock
      : formatEmailContent(plainText, userName);

    // 5. mailto:-URL zusammenbauen und zurückgeben.
    //    Der Aufrufer rendert einen echten <a href>-Link, den der Nutzer klickt –
    //    damit bleibt der Browser-Klick-Kontext erhalten (window.location.href
    //    nach einem await wird von Chrome/Edge als nicht-gesteuert blockiert).
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(finalSubject)}&body=${encodeURIComponent(emailContent)}`;

    if (mailtoUrl.length > 4000) {
      // Text zu lang für mailto: → .eml-Datei herunterladen
      downloadAsEml(finalSubject, emailContent);
      return null;
    }

    return { mailtoUrl, subject: finalSubject, content: emailContent };

  } catch (error) {
    console.error('Fehler beim Export als Email-Entwurf:', error);
    throw error;
  }
};

/**
 * Exportiert eine Bot-Nachricht als CSV-Datei.
 * Enthält zwei Spalten: „Betreff" und „Inhalt".
 *
 * @param {string} messageText - HTML-Inhalt der Bot-Nachricht
 * @param {string} [subject='Anfrage'] - Betreff-Spalte und Dateiname
 */
export const exportAsCsv = (messageText, subject = 'Anfrage') => {
  if (!messageText) {
    console.error('Keine Nachricht zum Exportieren vorhanden');
    return;
  }

  try {
    const plainText = removeHtmlTags(messageText);
    const csvContent = `Betreff,Inhalt\n"${escapeCSV(subject)}","${escapeCSV(plainText)}"`;

    const blob = new Blob([csvContent], { type: 'text/csv; charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${subject}_${Date.now()}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Fehler beim CSV-Export:', error);
    throw error;
  }
};

/**
 * Kopiert eine Bot-Nachricht mit Formatierung in die Zwischenablage.
 * Schreibt text/html + text/plain, damit Formatierung in Word/Outlook erhalten bleibt.
 *
 * @param {string} text - HTML-Inhalt der Bot-Nachricht
 */
export const copyToClipboard = (text) => {
  if (!text) {
    console.error('Kein Text zum Kopieren vorhanden');
    return;
  }

  try {
    const plainText = removeHtmlTags(text);

    if (typeof ClipboardItem !== 'undefined') {
      const htmlBlob = new Blob([text], { type: 'text/html' });
      const textBlob = new Blob([plainText], { type: 'text/plain' });
      navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })])
        .then(() => console.log('Text mit Formatierung in Zwischenablage kopiert'))
        .catch(err => {
          console.error('Fehler beim Kopieren mit Formatierung, Fallback auf Plaintext:', err);
          navigator.clipboard.writeText(plainText);
        });
    } else {
      navigator.clipboard.writeText(plainText).then(() => {
        console.log('Text in Zwischenablage kopiert (kein ClipboardItem-Support)');
      }).catch(err => {
        console.error('Fehler beim Kopieren in Zwischenablage:', err);
      });
    }
  } catch (error) {
    console.error('Fehler beim Copy-to-Clipboard:', error);
    throw error;
  }
};

// ─── Interne Hilfsfunktionen ─────────────────────────────────────────────────


/**
 * Wandelt HTML in sauberen Plaintext um.
 *
 * Konvertierungsregeln:
 *  - <br>, </p>, </div>, </li> → Zeilenumbruch
 *  - <li>                      → Aufzählungszeichen „• "
 *  - alle übrigen HTML-Tags    → entfernt
 *  - HTML-Entities             → dekodiert (via DOM)
 *  - mehr als zwei aufeinander­folgende Leerzeilen → auf eine reduziert
 *
 * @param {string} html - HTML-String
 * @returns {string} Bereinigter Plaintext
 */
const removeHtmlTags = (html) => {
  if (!html) return '';
  const div = document.createElement('div');
  const prepared = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ');
  div.innerHTML = prepared;
  const text = div.textContent || div.innerText || '';
  return text.replace(/\n{3,}/g, '\n\n').trim();
};

/**
 * Extrahiert den Betreff aus einer expliziten „Betreff:"- oder „Subject:"-Zeile.
 *
 * @param {string} text - Plaintext
 * @returns {string|null} Betreff oder null
 */
const extractEmailSubjectLine = (text) => {
  const match = text.match(/^Betreff\s*:\s*(.+)$/im);
  if (match) return match[1].trim();
  const matchEn = text.match(/^Subject\s*:\s*(.+)$/im);
  if (matchEn) return matchEn[1].trim();
  return null;
};

/**
 * Ermittelt einen Betreff-Kandidaten aus dem Text heuristisch:
 * Erste Zeile mit 4–99 Zeichen (ignoriert Leerzeilen und Einzeiler).
 * Wird als letzter Fallback vor dem hartkodierten Default verwendet.
 *
 * @param {string} text - Plaintext
 * @returns {string|null} Kandidat oder null
 */
const extractSubject = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  for (let line of lines) {
    line = line.trim();
    if (line.length > 3 && line.length < 100) return line;
  }
  return null;
};

/**
 * Fügt einem nicht-E-Mail-Text eine Grußformel mit Unterschrift hinzu.
 * Wird nur im Fallback-Pfad verwendet (wenn kein E-Mail-Block erkannt wurde
 * und der Nutzer trotzdem exportieren möchte).
 *
 * @param {string} text     - Plaintext der Nachricht
 * @param {string} userName - Vor- und Nachname des Nutzers; leer → „Robyflow"
 * @returns {string} Text + Grußformel
 */
const formatEmailContent = (text, userName = '') => {
  const plainText = text.trim();
  const signature = userName || 'Robyflow';
  return `${plainText}\n\nMit freundlichen Grüßen\n${signature}`;
};

/**
 * Escapet Sonderzeichen für CSV-Werte (RFC 4180).
 * Doppelte Anführungszeichen werden verdoppelt.
 *
 * @param {string} value - Rohwert
 * @returns {string} Escapeter Wert
 */
const escapeCSV = (value) => String(value).replace(/"/g, '""');
